import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DashboardIcon from "@mui/icons-material/Dashboard";
import assessment from "../../assets/images/method.png";
import exam from "../../assets/images/exam.png";
import test from "../../assets/images/test.png";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { useNavigate } from "react-router-dom";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import school from "../../assets/images/school.png";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { TableNodata } from "../../components/TableNodata";
import { TruncatedText } from "../../components/TruncatedText";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { OverAllLoader } from "../../components/OverAllLoader";
import UpcomingProctoredAssessments from "./UpcomingProctoredAssessments";
import TopPerformers from "./TopPerformers";
import { useChangeableItems, getLabelByKey, useOrganizations, useSelectedOrganizationId } from "../../store/useDynamicLabel";

// Fixed sizes for the Institute Management Overview table, so the section keeps the
// same height whether a search returns 5 rows, 2 rows or none.
const OVERVIEW_ROW_HEIGHT = 56;
const OVERVIEW_PAGINATION_HEIGHT = 56;

const SuperAdminDashboard = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  // Organization picked in the sidebar dropdown (institute / corporate) — only used for card labels
  const organizations = useOrganizations();
  const selectedOrganizationId = useSelectedOrganizationId();
  const isCorporate =
    organizations.find((org) => org.id === selectedOrganizationId)?.name === "corporate";

  const currentMonth = dayjs().month(); // 0-11
  const currentYear = dayjs().year();
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(+localStorage.getItem("currentpage") || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    +localStorage.getItem("rowsPerPage") || 5
  );
  const [testList, setTestList] = useState();
  const [tableData, setTableData] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(
    +localStorage.getItem("InstituteId") || ""
  );
  const { showSnackbar } = useSnackbar();
  const [colleges, setColleges] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];


  const commonAvatarStyle = {
    bgcolor: "#e8f2ff", // light blue background
    color: "#1e88e5", // blue text/icon color
    width: 24,
    height: 24,
    fontWeight: "bold",
    border: "2px solid #bbdefb",
  };

  const years = Array.from({ length: 6 }, (_, i) => currentYear - 2 + i); // Example: [2023, 2024, 2025, 2026, 2027, 2028]

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const navigateStudentsList = (instituteId, name) => {
    navigate("/students", {
      state: {
        instituteId: instituteId,
        dashboard: true,
        instituteName: name,
      },
    });
    localStorage.setItem("prevScreenDashboard", true);
  };

  const testCount = async () => {
    try {
      const res = await api.post("GetAllAssessmentsCountForSuperAdmin", {
        page: page || 0,
        size: rowsPerPage || 5,
        search_key: search || "",
      });
      if (res?.data?.status === "success") {
        setTestList(res?.data);
        setCount(res?.data?.total_institutes);
        setTableData(res?.data?.institute_information || []);
      }
    } catch (error) {
      console.log("Error : ", error);
    }
  };
  const instituteList = async () => {
    try {
      const res = await api.post("/ListInstituteNames");
      if (res?.data?.status === "success") {
        setColleges(res?.data?.data);
      }
    } catch (error) {
      console.log("Error :", error);
      showSnackbar("error", "Failed to fetch institutes.");
    }
  };

  useEffect(() => {
    instituteList();
  }, []);

  useEffect(() => {
    testCount();
  }, [page, rowsPerPage, search]);

  // Check login status
  const isLoggedIn = localStorage.getItem("logIn") !== "true";

  // Function to wrap content with Zoom conditionally
  // `in` comes from the props (every usage passes in={loaded}), so `loaded` flipping true
  // doesn't create a new component and remount the cards (the Upcoming card fetched twice).
  const ConditionalZoom = useCallback(({ children, style, key, in: inProp }) => {
    if (isLoggedIn) {
      return (
        <Zoom in={inProp} style={style} key={key}>
          {children}
        </Zoom>
      );
    }
    return children;
  }, [isLoggedIn]);

  return (
    <Box>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Grid>
            {/* Header Section */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <DashboardIcon sx={{ color: "#1e88e5", fontSize: 32 }} />
              <Typography
                variant="h4"
                fontWeight={700}
                sx={{ color: "#0a0a0a" }}
              >
                Dashboard
              </Typography>
            </Box>

            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{ mb: 4 }}
            >
              Overall summary of exam activities and {getLabel("STUDENT")}'s performance
              outcomes.
            </Typography>
          </Grid>
        </Box>

        {/* Cards Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "stretch",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
          }}
        >
          {/* Total Exams Card */}
          <ConditionalZoom
            in={loaded}
            style={{ transitionDelay: "400ms" }}
            key="total-exams-card"
          >
            <Card
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(40% - 16px)",
                  md: "1 1 calc(21% - 16px)",
                },
                borderRadius: "8px",
                border: "1px solid #c1c1c6ff",
                background: "#fff",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: 2,
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.03)",
                  boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                  background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                },
              }}
              onClick={() =>
                navigate("/questionPaperBuilder")
              }
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ color: "#333", lineHeight: 1.1 }}
                >
                  No. of {getLabel("QUESTION_PAPER")}
                </Typography>
                <Avatar sx={commonAvatarStyle}>
                  <Box
                    component="img"
                    src={test}
                    alt={`No. of ${getLabel("QUESTION_PAPER")}`}
                    sx={{ width: 26, height: 26 }}
                  />
                </Avatar>
              </Box>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#1e88e5", mt: 1 }}
              >
                {testList?.total_assessments || "0"}
              </Typography>
            </Card>
          </ConditionalZoom>

          {/* Self Assessments Card */}
          <ConditionalZoom
            in={loaded}
            style={{ transitionDelay: "400ms" }}
            key="self-assessments-card"
          >
            <Card
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(40% - 16px)",
                  md: "1 1 calc(21% - 16px)",
                },
                borderRadius: "8px",
                border: "1px solid #c1c1c6ff",
                background: "#fff",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: 2,
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.03)",
                  boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                  background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                },
              }}
              onClick={() =>
                navigate("/questionPaperBuilder", {
                  state: {
                    testType: "Self Assessment",
                    testTypeId: 1,
                  },
                })
              }
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ color: "#333", lineHeight: 1.1 }}
                >
                  No. of Self Assessments
                </Typography>
                <Avatar sx={commonAvatarStyle}>
                  <Box
                    component="img"
                    src={assessment}
                    alt="Self Assessments"
                    sx={{ width: 26, height: 26 }}
                  />
                </Avatar>
              </Box>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#1e88e5", mt: 1 }}
              >
                {testList?.total_self_assessments || "0"}
              </Typography>
            </Card>
          </ConditionalZoom>

          {/* Proctored Tests Card */}
          <ConditionalZoom
            in={loaded}
            style={{ transitionDelay: "400ms" }}
            key="proctored-tests-card"
          >
            <Card
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(40% - 16px)",
                  md: "1 1 calc(21% - 16px)",
                },
                borderRadius: "8px",
                border: "1px solid #c1c1c6ff",
                background: "#fff",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: 2,
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.03)",
                  boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                  background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                },
              }}
              onClick={() => (
                navigate("/questionPaperBuilder", {
                  state: {
                    testType: "Proctored Assessment",
                    testTypeId: 2,
                  },
                }),
                +localStorage.setItem("TabValue", 1)
              )}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ color: "#333", lineHeight: 1.1 }}
                >
                  No. of Proctored Assessments
                </Typography>
                <Avatar sx={commonAvatarStyle}>
                  <Box
                    component="img"
                    src={exam}
                    alt="Proctored Tests"
                    sx={{ width: 26, height: 26 }}
                  />
                </Avatar>
              </Box>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#1e88e5", mt: 1 }}
              >
                {testList?.total_proctored_assessments || "0"}
              </Typography>
            </Card>
          </ConditionalZoom>

          {/* Institutes Card */}
          <ConditionalZoom
            in={loaded}
            style={{ transitionDelay: "400ms" }}
            key="institutes-card"
          >
            <Card
              sx={{
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(40% - 16px)",
                  md: "1 1 calc(21% - 16px)",
                },
                borderRadius: "8px",
                border: "1px solid #c1c1c6ff",
                background: "#fff",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                p: 2,
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.03)",
                  boxShadow: "0px 6px 15px rgba(0,0,0,0.15)",
                  background: "linear-gradient(145deg, #f9fbff, #ffffff)",
                },
              }}
              onClick={() =>
                navigate("/institutes", {
                  state: {
                    viewInstitute: true,
                  },
                })
              }
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{ color: "#333", lineHeight: 1.1 }}
                >
                  {isCorporate ? "No. of Organizations" : "No. of Institutes"}
                </Typography>
                <Avatar sx={commonAvatarStyle}>
                  <Box
                    component="img"
                    src={school}
                    alt="Institutes"
                    sx={{ width: 26, height: 26 }}
                  />
                </Avatar>
              </Box>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#1e88e5", mt: 1 }}
              >
                {testList?.total_institutes || "0"}
              </Typography>
            </Card>
          </ConditionalZoom>
        </Box>

        {/* Institute Performance Overview Section */}
        <ConditionalZoom
          in={loaded}
          style={{ transitionDelay: "600ms" }}
          key="performance-overview-section"
        >
          <Box
            sx={{
              p: 2,
              borderRadius: "8px",
              border: "1px solid #c1c1c6ff",
              background: "#fff",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
            }}
          >
            <Grid
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Grid
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
              >
                <SchoolOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  {getLabel("INSTITUTE")} performance Overview
                </Typography>
              </Grid>

              {isLoggedIn && (
                <Grid
                  container
                  spacing={2}
                  sx={{ mb: 3, alignItems: "center" }}
                >
                  {/* Institute Dropdown */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      size="small"
                      sx={{
                        width: "100%",
                        "& .MuiOutlinedInput-root": {
                          height: 40,
                          width: 250,
                        },
                      }}
                      options={colleges}
                      getOptionLabel={(option) => option?.name || ""}
                      value={
                        colleges.find(
                          (college) =>
                            college.institute_id === selectedCollege
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        const selectedId = newValue
                          ? newValue.institute_id
                          : "";
                        localStorage.setItem(
                          "Institute_Id_Filter",
                          selectedId
                        );
                        setPage(0);
                        setSelectedCollege(selectedId);
                      }}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          className="custom-search-field"
                          {...params}
                          placeholder="All Institutes"
                          variant="outlined"
                          sx={{
                            "& .MuiInputBase-input": {
                              fontSize: "12px",
                            },
                            "& .MuiInputLabel-root": {
                              fontSize: "12px",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Month Dropdown */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" sx={{ height: 40 }}>
                      <InputLabel sx={{ fontSize: "12px" }}>Month</InputLabel>
                      <Select
                        value={selectedMonth ?? ""}
                        onChange={(e) =>
                          setSelectedMonth(
                            e.target.value !== "" ? e.target.value : null
                          )
                        }
                        displayEmpty
                        input={
                          <OutlinedInput
                            label="Month"
                            startAdornment={
                              <InputAdornment position="start">
                                <CalendarMonthIcon fontSize="small" />
                              </InputAdornment>
                            }
                          />
                        }
                        sx={{
                          "& .MuiSelect-select": { fontSize: "12px" },
                          "& .MuiOutlinedInput-root": { height: 40 },
                        }}
                      >
                        <MenuItem value="">All Months</MenuItem>
                        {months.map((month, index) => (
                          <MenuItem key={index} value={index}>
                            {month}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Year Dropdown */}
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small" sx={{ height: 40 }}>
                      <InputLabel sx={{ fontSize: "12px" }}>Year</InputLabel>
                      <Select
                        value={selectedYear ?? ""}
                        onChange={(e) =>
                          setSelectedYear(
                            e.target.value !== "" ? e.target.value : null
                          )
                        }
                        displayEmpty
                        input={
                          <OutlinedInput
                            label="Year"
                            startAdornment={
                              <InputAdornment position="start">
                                <CalendarMonthIcon fontSize="small" />
                              </InputAdornment>
                            }
                          />
                        }
                        sx={{
                          "& .MuiSelect-select": { fontSize: "12px" },
                          "& .MuiOutlinedInput-root": { height: 40 },
                        }}
                      >
                        <MenuItem value="">All Years</MenuItem>
                        {years.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Grid>

            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 3,
                width: "100%",
              }}
            >
              {/* Top Performers Card */}
              <ConditionalZoom
                in={loaded}
                style={{ transitionDelay: "800ms" }}
                key="top-performers-card-inner"
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TopPerformers showInstituteFilter institutes={colleges} />
                </Box>
              </ConditionalZoom>

              <ConditionalZoom
                in={loaded}
                style={{ transitionDelay: "800ms" }}
                key="upcoming-proctored-assessments-card-inner"
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* Proctored assessments from all institutes, with an Institute filter */}
                  <UpcomingProctoredAssessments showInstituteFilter institutes={colleges} />
                </Box>
              </ConditionalZoom>
            </Box>
          </Box>
        </ConditionalZoom>

        {/* Institute Management Overview Section */}
        <ConditionalZoom
          in={loaded}
          style={{ transitionDelay: "1000ms" }}
          key="institute-management-overview"
        >
          <Box
            sx={{
              mt: 3,
              p: 3,
              borderRadius: "8px",
              border: "1px solid #c1c1c6ff",
              background: "#fff",
              boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
              overflowX: "auto",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <SchoolOutlinedIcon sx={{ color: "#1e88e5", fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  {getLabel("INSTITUTE")} Management Overview
                </Typography>
              </Box>
              <TextField
                value={search}
                onChange={(e) => {
                  setPage(0);
                  setSearch(e.target.value);
                }}
                size="small"
                variant="outlined"
                placeholder={`Search ${getLabel("INSTITUTE")}...`}
                className="custom-search-field"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Table Header */}
            <Grid
              container
              sx={{
                py: 1.5,
                borderBottom: "1px solid #e0e0e0",
                fontWeight: 600,
                color: "text.secondary",
                flexWrap: "nowrap",
                minWidth: 760,
              }}
            >
              <Grid item xs={4} sx={{ width: "30%" }}>
                {getLabel("INSTITUTE")}
              </Grid>
              <Grid item xs={2} sx={{ width: "15%" }}>
                {getLabel("INSTITUTE_ADMIN")}
              </Grid>
              <Grid item xs={2} sx={{ width: "12%" }}>
                {getLabel("TRAINER")}
              </Grid>
              <Grid item xs={2} sx={{ width: "12%" }}>
                {getLabel("STUDENT")}
              </Grid>
              <Grid item xs={2} sx={{ width: "15%" }}>
                {getLabel("QUESTION_PAPER")}
              </Grid>
              <Grid item xs={2} sx={{ width: "10%" }}>
                Status
              </Grid>
            </Grid>

            {/* Always reserves room for a full page of rows (CSS min-height) */}
            <Box sx={{ minHeight: rowsPerPage * OVERVIEW_ROW_HEIGHT }}>
            {/* Institute Data */}
            {tableData.map((institute, index) => (
              <Grid
                container
                key={`institute-${index}`}
                alignItems="center"
                sx={{
                  py: 1.5,
                  height: OVERVIEW_ROW_HEIGHT,
                  boxSizing: "border-box",
                  borderBottom: "1px solid #f2f2f2",
                  "&:hover": { backgroundColor: "#fafafa" },
                  flexWrap: "nowrap",
                  minWidth: 760,
                }}
              >
                {/* Institute Info */}
                <Grid
                  item
                  xs={4}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    width: "30%",
                    minWidth: 0,
                  }}
                >
                  <Avatar sx={{ ...commonAvatarStyle, flexShrink: 0 }}>
                    {institute?.name?.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <TruncatedText text={institute?.name} sx={{ fontWeight: 600 }} />
                  </Box>
                </Grid>

                {/* Institute Admins */}
                <Grid
                  item
                  xs={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    width: "15%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigate("/instituteAdmin", {
                        state: {
                          instituteId: institute?.institute_id,
                          dashboard: true,
                          institute_Name: institute?.name,
                        },
                      })
                    }
                  >
                    <GroupOutlinedIcon
                      sx={{ color: "#1e88e5", fontSize: 20 }}
                    />
                    {institute?.admin_count || "0"}
                  </Box>
                </Grid>

                {/* Trainers */}
                <Grid
                  item
                  xs={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    width: "12%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigate("/trainers", {
                        state: {
                          instituteId: institute?.institute_id,
                          dashboard: true,
                          instituteName: institute?.name,
                        },
                      })
                    }
                  >
                    <GroupOutlinedIcon
                      sx={{ color: "#1e88e5", fontSize: 20 }}
                    />
                    {institute?.trainer_count || "0"}
                  </Box>
                </Grid>

                {/* Students */}
                <Grid
                  item
                  xs={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    width: "12%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigateStudentsList(
                        institute?.institute_id,
                        institute?.name
                      )
                    }
                  >
                    <GroupOutlinedIcon
                      sx={{ color: "#1e88e5", fontSize: 20 }}
                    />
                    {institute?.student_count || "0"}
                  </Box>
                </Grid>

                {/* Question Papers */}
                <Grid
                  item
                  xs={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    width: "15%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigate("/questionPaperBuilder", {
                        state: {
                          institute_id: institute?.institute_id,
                          instituteName: institute?.name,
                        },
                      })
                    }
                  >
                    <DescriptionOutlinedIcon
                      sx={{ color: "#1e88e5", fontSize: 20 }}
                    />
                    <Typography fontWeight={500}>
                      {institute?.assessment_count || "0"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Status */}
                <Grid item xs={2} sx={{ width: "10%" }}>
                  <Box
                    sx={{
                      display: "inline-block",
                      px: 2,
                      py: 0.5,
                      borderRadius: "12px",
                      fontWeight: "bold",
                      fontSize: "12px",
                      backgroundColor:
                        institute.status === "ACTIVE"
                          ? "#d4edda"
                          : institute.status === "INACTIVE"
                            ? "#f8d7da"
                            : "#f1f1f1",
                      color:
                        institute.status === "ACTIVE"
                          ? "#155724"
                          : institute.status === "INACTIVE"
                            ? "#721c24"
                            : "#333",
                      textAlign: "center",
                    }}
                  >
                    {institute.status}
                  </Box>
                </Grid>
              </Grid>
            ))}

            {tableData.length === 0 && (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <TableNodata />
              </Box>
            )}
            </Box>
            <Box sx={{ minHeight: OVERVIEW_PAGINATION_HEIGHT }}>
            {((page === 0 && tableData?.length > rowsPerPage - 1) ||
              page !== 0) && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                  }}
                >
                  {page !== 0 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Button
                        variant="outlined"
                        startIcon={
                          <ArrowBackIcon
                            sx={{
                              color: page === 0 ? "#a2a2a2" : "#414651",
                            }}
                          />
                        }
                        disabled={page === 0}
                        onClick={() => setPage(page - 1)}
                        sx={{
                          borderRadius: "12px",
                          textTransform: "none",
                          color: page === 0 ? "#a2a2a2" : "#414651",
                          borderColor: "#d0d0d0",
                        }}
                      >
                        Previous
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ width: "110px" }} />
                  )}

                  <Pagination
                    page={page + 1}
                    onChange={(e, value) => setPage(value - 1)}
                    count={Math.ceil(count / rowsPerPage)}
                    siblingCount={3}
                    boundaryCount={3}
                    shape="rounded"
                    hidePrevButton
                    hideNextButton
                  />
                  {page + 1 !== Math.ceil(count / rowsPerPage) ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Button
                        variant="outlined"
                        endIcon={
                          <ArrowForwardIcon
                            sx={{
                              color: page + 1 === count ? "#a2a2a2" : "#414651",
                            }}
                          />
                        }
                        disabled={page + 1 === count}
                        onClick={() => setPage(page + 1)}
                        sx={{
                          borderRadius: "12px",
                          textTransform: "none",
                          color: page + 1 === count ? "#a2a2a2" : "#414651",
                          borderColor: "#d0d0d0",
                        }}
                      >
                        Next
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ width: "110px" }} />
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </ConditionalZoom>
      </Box>
    </Box>
  );
};

export default SuperAdminDashboard;
